import Pusher from "pusher-js";
import Config from "../config";
import debug from "./notificationDebug";
import clientLog from "./clientLog";

// Bağlantı "connected" dışında bir durumda bu süreyi aşarsa zorla
// disconnect + connect yapılır. Ardışık denemelerde katlanarak artar.
const FORCE_RECONNECT_AFTER_MS = 15000;
const FORCE_RECONNECT_MAX_MS = 60000;
const WATCHDOG_INTERVAL_MS = 5000;

// Soket "connected" görünüp hiç trafik gelmiyorsa (uyku sonrası ölü soket)
// bu süreden sonra bağlantı şüpheli sayılır ve yenilenir.
const SILENCE_LIMIT_MS = 150000;

// Bundan uzun süren kopukluklar düzeldiğinde loglanır (kısa dalgalanmalar değil).
const OUTAGE_LOG_MIN_MS = 30000;

// pusher-js "failed" durumundan kendiliğinden çıkmaz (aşağıdaki açıklama);
// örnek sıfırdan kurulur. Art arda düşüşlerde bekleme katlanarak artar.
const RECREATE_BASE_MS = 5000;
const RECREATE_MAX_MS = 60000;
// Bu kadar süre bağlı kalan bağlantı sağlıklı sayılır, bekleme sıfırlanır.
const RECREATE_STABLE_MS = 5 * 60000;

let pusher = null;
let watchdogTimer = null;
let downSince = null;
let forceDelay = FORCE_RECONNECT_AFTER_MS;
let lastActivityAt = Date.now();
let browserEventsBound = false;
let outageStartedAt = null;
let recreateTimer = null;
let recreateDelay = RECREATE_BASE_MS;
let connectedAt = null;
// Bu Pusher örneğinde subscription_error dinleyicisi bağlanmış kanallar.
let errorBoundChannels = new Set();
// Bu Pusher örneğinde yedek olarak abone olunan eski açık kanallar.
let fallbackChannels = new Set();

// channelName::eventName -> { channelName, eventName, handler }
const bindings = new Map();
const stateListeners = new Set();
const resumeListeners = new Set();

function notifyState(current, previous) {
  stateListeners.forEach(cb => {
    try {
      cb(current, previous);
    } catch (e) {
      /* dinleyici hatası bağlantıyı etkilemesin */
    }
  });
}

function notifyResume(reason) {
  resumeListeners.forEach(cb => {
    try {
      cb(reason);
    } catch (e) {
      /* yoksay */
    }
  });
}

function readAuthToken() {
  try {
    return localStorage.getItem("authToken") || "";
  } catch (e) {
    return "";
  }
}

function getState() {
  return pusher ? pusher.connection.state : "initialized";
}

function forceReconnect() {
  if (!pusher) return;
  debug("realtime: zorla yeniden bağlanma", { state: getState() });
  try {
    pusher.disconnect();
  } catch (e) {
    /* yoksay */
  }
  try {
    pusher.connect();
  } catch (e) {
    /* yoksay */
  }
}

// "failed" kalıcıdır: pusher-js, açıldıktan kısa süre sonra (2 x activityTimeout
// içinde) temiz kapanmadan kopan bağlantıları taşıma katmanının "ölümü" sayar
// ve 2 ölümden sonra WebSocket'i desteklenmiyor kabul eder. Bundan sonra
// connect() doğrudan "failed" döner; disconnect/connect işe yaramaz, sayfa
// yenilenene kadar canlı bildirim gelmez. Tek çıkış yeni bir Pusher örneği.
function scheduleRecreate(reason) {
  if (recreateTimer) return;
  const delay = recreateDelay + Math.floor(Math.random() * 2000);
  recreateDelay = Math.min(recreateDelay * 2, RECREATE_MAX_MS);
  debug("realtime: yeniden kurulacak", { reason, duration: delay });
  clientLog.warn("realtime.recreate", { reason, delayMs: delay });
  recreateTimer = setTimeout(() => {
    recreateTimer = null;
    recreate();
  }, delay);
}

function recreate() {
  const old = pusher;
  pusher = null;
  if (old) {
    try {
      // Eski örneğin olayları (disconnected vb.) arayüze sızmasın.
      old.connection.unbind_all();
      old.disconnect();
    } catch (e) {
      /* yoksay */
    }
  }
  errorBoundChannels = new Set();
  fallbackChannels = new Set();
  start();
  bindings.forEach(binding => attach(binding));
}

function attach(binding) {
  const channel = pusher.subscribe(binding.channelName);
  channel.bind(binding.eventName, binding.handler);
  if (!errorBoundChannels.has(binding.channelName)) {
    errorBoundChannels.add(binding.channelName);
    channel.bind("pusher:subscription_error", status => {
      clientLog.error("realtime.subscription_error", {
        channel: binding.channelName,
        message: String(
          status && typeof status === "object"
            ? status.status || status.type || JSON.stringify(status)
            : status
        ).slice(0, 200)
      });
      useFallback(binding);
    });
  }
  return channel;
}

// private- kanal imzası alınamazsa (API'deki PUSHER_SECRET soketi'deki ile
// uyuşmuyor, /pusher/auth hata veriyor...) geçiş süresince eski açık kanal
// dinlenir: bildirimler kesilmez, durum loglanır. Sunucu eski kanala
// göndermeyi bırakınca (PUSHER_LEGACY_PUBLIC_CHANNELS=false) bu yol boşa düşer.
function useFallback(binding) {
  const fallback = binding.fallbackChannel;
  if (!fallback || !pusher || fallbackChannels.has(fallback)) return;
  fallbackChannels.add(fallback);
  clientLog.warn("realtime.fallback", { channel: binding.channelName });
  pusher.subscribe(fallback).bind(binding.eventName, binding.handler);
}

// Sessizlik her zaman ölü bağlantı demek değil: arka plandaki sekmede Chrome
// zamanlayıcıları dakikada bire indirir, pusher'ın ping'i gecikir. Sağlam
// bağlantıyı koparmak (ve o arada gelen mesajı kaçırmak) yerine önce ping
// atılır; pong pongTimeout içinde gelmezse pusher-js bağlantıyı kendisi yeniler.
function probeConnection() {
  const manager = pusher && pusher.connection;
  if (
    manager &&
    manager.connection &&
    typeof manager.sendActivityCheck === "function"
  ) {
    try {
      manager.sendActivityCheck();
      return;
    } catch (e) {
      /* aşağıdaki yola düş */
    }
  }
  forceReconnect();
}

function checkConnection(immediate) {
  if (!pusher) return;
  const current = getState();

  if (current === "failed") {
    scheduleRecreate("failed");
    return;
  }

  if (current === "connected") {
    downSince = null;
    forceDelay = FORCE_RECONNECT_AFTER_MS;

    // Ölü soket kontrolü: bağlı görünüyor ama uzun süredir hiç paket yok.
    if (Date.now() - lastActivityAt > SILENCE_LIMIT_MS) {
      clientLog.info("realtime.silent_socket", {
        silentMs: Date.now() - lastActivityAt,
        hidden: document.hidden
      });
      lastActivityAt = Date.now();
      probeConnection();
    }
    return;
  }

  if (!downSince) downSince = Date.now();

  if (immediate || Date.now() - downSince >= forceDelay) {
    downSince = Date.now();
    forceDelay = Math.min(forceDelay * 2, FORCE_RECONNECT_MAX_MS);
    forceReconnect();
  }
}

function startWatchdog() {
  if (watchdogTimer) return;
  watchdogTimer = setInterval(
    () => checkConnection(false),
    WATCHDOG_INTERVAL_MS
  );
}

function onOnline() {
  lastActivityAt = Date.now();
  checkConnection(true);
  notifyResume("online");
}

function onVisibilityChange() {
  if (document.visibilityState !== "visible") return;
  // Sekme arka plandayken timer'lar kısılır; öne gelince hemen doğrula.
  checkConnection(getState() !== "connected");
  notifyResume("visible");
}

function onFocus() {
  notifyResume("focus");
}

function bindBrowserEvents() {
  if (browserEventsBound) return;
  browserEventsBound = true;
  window.addEventListener("online", onOnline);
  window.addEventListener("focus", onFocus);
  document.addEventListener("visibilitychange", onVisibilityChange);
}

function start() {
  if (pusher) return pusher;

  pusher = new Pusher(Config.PUSHER_APP_KEY, {
    wsHost: Config.PUSHER_WS_HOST,
    wsPort: Config.PUSHER_WS_PORT,
    wssPort: Config.PUSHER_WSS_PORT,
    forceTLS: false,
    encrypted: false,
    disableStats: true,
    enabledTransports: ["wss", "ws"],
    // 20 sn sessizlikte ping atılır, ölü bağlantı ~30 sn içinde fark edilir.
    // 30 sn idi: pusher-js "2 x activityTimeout" (60 sn) dolmadan kopan
    // bağlantıyı ölüm sayıyor; proxy'nin ~60 sn'de kestiği bağlantılar iki
    // turda "failed"a düşürüyordu. 20 sn'de eşik 40 sn olur, sık ping de
    // boşta bağlantı kesen ara cihazları canlı tutar.
    activityTimeout: 20000,
    pongTimeout: 10000,
    unavailableTimeout: 10000,
    // private- kanallar için abonelik imzası (API: /pusher/auth). Sabit
    // headers kullanılır: headersProvider pusher-js 7.6'da geldi; sunucuda
    // derlenen sürüm daha eski olduğunda yok sayılıp token gönderilmiyordu
    // (401). Token örnek kurulurken okunur; giriş sonrası sayfa zaten yenilenir,
    // yeniden kurulan örnek (recreate) de güncel token'ı alır.
    channelAuthorization: {
      endpoint: Config.endpoint + "/pusher/auth",
      transport: "ajax",
      headers: { Authorization: readAuthToken() }
    }
  });

  pusher.connection.bind("state_change", states => {
    if (states.current === "connected") {
      connectedAt = Date.now();
      lastActivityAt = Date.now();
      downSince = null;
      forceDelay = FORCE_RECONNECT_AFTER_MS;
      if (outageStartedAt) {
        const downMs = Date.now() - outageStartedAt;
        if (downMs >= OUTAGE_LOG_MIN_MS) {
          clientLog.info("realtime.recovered", {
            downMs,
            hidden: document.hidden
          });
        }
        outageStartedAt = null;
      }
    } else {
      // Uzun süre ayakta kalan bağlantıdan sonraki düşüş yeni bir olaydır;
      // yeniden kurma beklemesi baştan başlar.
      if (connectedAt && Date.now() - connectedAt >= RECREATE_STABLE_MS) {
        recreateDelay = RECREATE_BASE_MS;
      }
      connectedAt = null;
      if (!outageStartedAt) outageStartedAt = Date.now();
    }
    // unavailable: sunucuya ulaşılamıyor; failed: tarayıcı WebSocket'i
    // desteklemiyor ya da bir proxy/eklenti engelliyor.
    if (states.current === "unavailable") {
      clientLog.warn("realtime.unavailable", { previous: states.previous });
    } else if (states.current === "failed") {
      clientLog.error("realtime.failed", { previous: states.previous });
      scheduleRecreate("failed");
    }
    debug("realtime: durum", states.previous + " -> " + states.current);
    notifyState(states.current, states.previous);
  });

  pusher.connection.bind("message", message => {
    lastActivityAt = Date.now();
    // Pusher'ın kendi ping/pong'u değilse hangi kanaldan ne geldiğini göster.
    if (message && message.channel) {
      debug("realtime: soket mesajı", {
        channel: message.channel,
        event: message.event
      });
    }
  });

  pusher.connection.bind("error", err => {
    // Hata kanalı sessiz kalırsa pusher-js unhandled error basar.
    // PusherError: { type, data: { code, message } }
    // WebSocketError: { type, error: Event }
    const inner = (err && err.error) || {};
    const data =
      err && err.data && typeof err.data === "object" ? err.data : {};
    const type = (err && err.type) || inner.type || null;
    // WebSocketError kod/mesaj taşımaz ve her kopmada hemen ardından kapanış
    // koduyla (ör. 1006) bir PusherError gelir; aynı olay iki satır olmasın.
    if (type !== "WebSocketError") {
      clientLog.warn("realtime.error", {
        type,
        code: data.code || null,
        message: String(data.message || inner.message || "").slice(0, 200)
      });
    }
    checkConnection(false);
  });

  bindBrowserEvents();
  startWatchdog();

  return pusher;
}

export default {
  start,
  getState,

  /**
   * Aynı kanal+event için ikinci kez bind yapmaz.
   * (Eski kodda her excelUploadId olayında yeniden bind edilip mesajlar
   * çoğalıyordu.)
   */
  /**
   * options.fallbackChannel: private kanal imzası alınamazsa dinlenecek eski
   * açık kanal (geçiş süresi için).
   */
  subscribe(channelName, eventName, handler, options) {
    if (!channelName || channelName.indexOf("undefined") !== -1) {
      // Bu kanal açılmazsa kullanıcı hiçbir canlı bildirim alamaz.
      clientLog.error("realtime.invalid_channel", {
        message: String(channelName),
        event: eventName
      });
      return null;
    }
    start();

    const key = channelName + "::" + eventName;
    if (bindings.has(key)) return pusher.channel(channelName);

    const binding = {
      channelName,
      eventName,
      handler,
      fallbackChannel: (options && options.fallbackChannel) || null
    };
    bindings.set(key, binding);
    const channel = attach(binding);
    debug("realtime: abone olundu", key);
    return channel;
  },

  unsubscribe(channelName) {
    if (!pusher || !channelName) return;
    bindings.forEach((value, key) => {
      if (value.channelName === channelName) bindings.delete(key);
    });
    errorBoundChannels.delete(channelName);
    try {
      pusher.unsubscribe(channelName);
    } catch (e) {
      /* yoksay */
    }
  },

  // Bağlantı durumu değiştikçe çağrılır. Aboneliği kaldıran fonksiyon döner.
  onStateChange(cb) {
    stateListeners.add(cb);
    return () => stateListeners.delete(cb);
  },

  // Sekme öne geldiğinde / internet geri geldiğinde çağrılır.
  onResume(cb) {
    resumeListeners.add(cb);
    return () => resumeListeners.delete(cb);
  },

  ping() {
    checkConnection(false);
  },

  stop() {
    if (recreateTimer) {
      clearTimeout(recreateTimer);
      recreateTimer = null;
    }
    if (watchdogTimer) {
      clearInterval(watchdogTimer);
      watchdogTimer = null;
    }
    if (browserEventsBound) {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      browserEventsBound = false;
    }
    bindings.clear();
    errorBoundChannels = new Set();
    fallbackChannels = new Set();
    if (pusher) {
      try {
        pusher.disconnect();
      } catch (e) {
        /* yoksay */
      }
      pusher = null;
    }
  }
};
